import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactECharts from 'echarts-for-react';
import {
  getProjectMetricsDistribution,
  ProjectMetricsDistributionResponse,
  PortfolioMetricDistribution,
  MetricDistributionBin,
} from '../../services/api';

interface ProjectPortfolioMetricsDonutsProps {
  parentEntityId: number;
}

const ENTITY_TOOLTIP_MAX = 25;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Same pattern as AttributeDemographicsTab buildDistributionTooltipHtml: stats then • per row. */
function buildPortfolioBinTooltipHtml(
  binLabel: string,
  count: number,
  percent: number,
  projectNames: string[] | undefined,
): string {
  const parts: string[] = [`<b>${escapeHtml(binLabel)}</b>`];
  parts.push(escapeHtml(`${count} (${percent.toFixed(1)}%)`));
  if (projectNames?.length) {
    projectNames.slice(0, ENTITY_TOOLTIP_MAX).forEach((name) => {
      parts.push(`• ${escapeHtml(name)}`);
    });
    if (projectNames.length > ENTITY_TOOLTIP_MAX) {
      parts.push(`• ${escapeHtml(`… +${projectNames.length - ENTITY_TOOLTIP_MAX} more`)}`);
    }
  }
  return parts.join('<br/>');
}

/** Aligns with Demographics: variation → single bucket → no data. */
function metricSortTier(m: PortfolioMetricDistribution): 0 | 1 | 2 {
  if (m.project_count <= 0) {
    return 2;
  }
  const activeBins = m.bins.filter((b) => b.count > 0).length;
  if (activeBins >= 2) {
    return 0;
  }
  return 1;
}

/** Metrics with multiple populated bins first, then one bin only, then no projects. */
function sortPortfolioMetrics(metrics: PortfolioMetricDistribution[]): PortfolioMetricDistribution[] {
  return [...metrics].sort((a, b) => {
    const ta = metricSortTier(a);
    const tb = metricSortTier(b);
    if (ta !== tb) {
      return ta - tb;
    }
    if (ta === 0) {
      const ba = a.bins.filter((x) => x.count > 0).length;
      const bb = b.bins.filter((x) => x.count > 0).length;
      if (ba !== bb) {
        return bb - ba;
      }
    }
    if (ta === 1 && a.project_count !== b.project_count) {
      return b.project_count - a.project_count;
    }
    return a.metric_name.localeCompare(b.metric_name);
  });
}

function buildDonutOption(metric: PortfolioMetricDistribution) {
  const palette = ['#1976d2', '#7b1fa2', '#00897b', '#f9a825', '#c62828', '#5d4037'];
  const orgLowerThreshold =
    metric.org_threshold_lower_bound ?? metric.org_lower_bound ?? metric.lower_bound ?? null;
  const orgUpperThreshold =
    metric.org_threshold_upper_bound ?? metric.org_upper_bound ?? metric.upper_bound ?? null;
  const isPercentageMetric = (metric.value_type || '').toLowerCase() === 'percentage';
  const formatThresholdValue = (value: number | null): string => {
    if (value === null) return 'N/A';
    if (isPercentageMetric) {
      const normalizedPercent = Math.abs(value) <= 1 ? value * 100 : value;
      return `${normalizedPercent.toFixed(2)}%`;
    }
    return value.toFixed(2);
  };
  const thresholdText =
    orgLowerThreshold !== null || orgUpperThreshold !== null
      ? `Org Thresholds: ${
          formatThresholdValue(orgLowerThreshold)
        } - ${formatThresholdValue(orgUpperThreshold)}`
      : '';
  return {
    title: {
      text: metric.metric_name,
      subtext:
        metric.project_count > 0
          ? `${metric.project_count} project${metric.project_count === 1 ? '' : 's'} · ${
              metric.value_type || 'values'
            }${thresholdText ? `\n${thresholdText}` : ''}`
          : thresholdText || undefined,
      left: 'center',
      top: 0,
      textStyle: { fontSize: 13, fontWeight: 600 },
      subtextStyle: { fontSize: 11, color: '#666' },
    },
    tooltip: {
      trigger: 'item',
      renderMode: 'html',
      formatter: (params: { name: string; value: number; percent: number }) => {
        const bin = metric.bins.find((b) => b.label === params.name);
        const names = bin?.project_names?.filter(Boolean) ?? [];
        return buildPortfolioBinTooltipHtml(
          params.name,
          params.value,
          params.percent,
          names.length ? names : undefined,
        );
      },
    },
    series: [
      {
        type: 'pie',
        radius: ['36%', '61%'],
        center: ['50%', '60%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 4,
          borderColor: '#fff',
          borderWidth: 1,
        },
        label: { fontSize: 10 },
        data: metric.bins
          .filter((b) => b.count > 0)
          .map((b, i) => ({
            name: b.label,
            value: b.count,
            itemStyle: { color: palette[i % palette.length] },
          })),
      },
    ],
  };
}

function getOrgThresholdDisplay(metric: PortfolioMetricDistribution): string | null {
  const orgLowerThreshold =
    metric.org_threshold_lower_bound ?? metric.org_lower_bound ?? metric.lower_bound ?? null;
  const orgUpperThreshold =
    metric.org_threshold_upper_bound ?? metric.org_upper_bound ?? metric.upper_bound ?? null;
  const isPercentageMetric = (metric.value_type || '').toLowerCase() === 'percentage';
  const formatThresholdValue = (value: number | null): string => {
    if (value === null) return 'N/A';
    if (isPercentageMetric) {
      const normalizedPercent = Math.abs(value) <= 1 ? value * 100 : value;
      return `${normalizedPercent.toFixed(2)}%`;
    }
    return value.toFixed(2);
  };

  if (orgLowerThreshold === null && orgUpperThreshold === null) {
    return null;
  }
  return `Org Thresholds: ${formatThresholdValue(orgLowerThreshold)} - ${formatThresholdValue(
    orgUpperThreshold
  )}`;
}

function BinProjectNames({ bin }: { bin: MetricDistributionBin }) {
  const names = bin.project_names?.filter(Boolean) ?? [];
  return (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, lineHeight: 1.45 }}>
      <Box component="span" fontWeight={600} color="text.primary">
        {bin.label}
      </Box>
      : {names.length ? names.join(', ') : '—'}
    </Typography>
  );
}

const ProjectPortfolioMetricsDonuts: React.FC<ProjectPortfolioMetricsDonutsProps> = ({
  parentEntityId,
}) => {
  const [data, setData] = useState<ProjectMetricsDistributionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectTypeTab, setProjectTypeTab] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getProjectMetricsDistribution(parentEntityId);
        if (!cancelled) {
          setData(res.data);
          setProjectTypeTab(0);
        }
      } catch (e: unknown) {
        const err = e as { response?: { data?: { detail?: string } }; message?: string };
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load portfolio metrics');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [parentEntityId]);

  useEffect(() => {
    if (data?.project_type_groups?.length && projectTypeTab >= data.project_type_groups.length) {
      setProjectTypeTab(0);
    }
  }, [data, projectTypeTab]);

  const groups = data?.project_type_groups;
  const activeGroup = groups?.[projectTypeTab];

  const sortedDimensions = useMemo(() => {
    if (!activeGroup?.dimensions?.length) {
      return [];
    }
    return activeGroup.dimensions.map((dim) => ({
      ...dim,
      metrics: sortPortfolioMetrics(dim.metrics),
    }));
  }, [activeGroup]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || !groups?.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No leaf project entities with computed metrics were found under this selection, or there is no data yet.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Portfolio view uses leaf <strong>PROJECT</strong> entities grouped by project type (e.g. Development,
          Operations).
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Distributions use the <strong>latest project-level</strong> computed value per project
        (Sprint, Team, Module, and other in-project grains are excluded). Percentage metrics use
        magnitude (absolute value) in bins 0–5%, 5–25%, 25–50%, 50–100%, and &gt;100%. Other types
        use equal-width bins across observed values. Hover a slice or read the list under each chart
        for project names. Open <strong>Project level metrics</strong> to select a project and drill
        down by metric level.
      </Typography>

      <Tabs
        value={projectTypeTab}
        onChange={(_, v) => setProjectTypeTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {groups.map((g) => (
          <Tab key={g.entity_type_id} label={g.display_label} sx={{ textTransform: 'none' }} />
        ))}
      </Tabs>

      {activeGroup && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: 'divider',
          }}
        >
          {activeGroup.dimensions.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No metrics with data for this project type.
            </Typography>
          ) : (
            sortedDimensions.map((dim) => (
              <Accordion
                key={dim.review_dimension}
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{
                  mb: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  '&:last-of-type': { mb: 0 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: 'action.hover',
                    '& .MuiAccordionSummary-content': { my: 1 },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                    {dim.review_dimension}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                      ({dim.metrics.length} metric{dim.metrics.length === 1 ? '' : 's'})
                    </Typography>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                  <Grid container spacing={2}>
                    {dim.metrics.map((m) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={`${dim.review_dimension}-${m.metric_name}`}>
                        {(() => {
                          const thresholdDisplay = getOrgThresholdDisplay(m);
                          return (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <ReactECharts
                            option={buildDonutOption(m)}
                            style={{ height: 220, width: '100%', flexShrink: 0 }}
                            opts={{ renderer: 'svg' }}
                            notMerge
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, px: 0.5, display: 'block' }}
                          >
                            {thresholdDisplay || 'Org Thresholds: Not configured'}
                          </Typography>
                          <Box
                            sx={{
                              mt: 1,
                              px: 0.5,
                              maxHeight: 140,
                              overflow: 'auto',
                              flex: 1,
                            }}
                          >
                            {m.bins.map((b) => (
                              <BinProjectNames key={b.label} bin={b} />
                            ))}
                          </Box>
                        </Paper>
                          );
                        })()}
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ProjectPortfolioMetricsDonuts;
